import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, RotateCcw } from "lucide-react";

const FIELDS = [["re_line", "RE: line", Input], ["body", "Request paragraphs", Textarea], ["on_behalf", "On behalf of", Textarea], ["closing", "Closing", Textarea]];

export function RequestLetterDialog({ job, onClose }) {
  const [defaults, setDefaults] = useState(null);
  const [text, setText] = useState({});
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState("");
  const [includeDetails, setIncludeDetails] = useState(true);
  useEffect(() => {
    api.get(`/jobs/${job.id}/request-letter/text`).then((r) => { setDefaults(r.data.defaults); setText({ ...r.data.defaults, ...r.data.saved }); setDetails(r.data.details || ""); setIncludeDetails(r.data.saved?.include_details ?? true); }).catch(() => toast.error("Could not load letter text"));
  }, [job.id]);

  const generate = async () => {
    setBusy(true);
    const win = window.open("", "_blank");
    try {
      const edits = Object.fromEntries(Object.entries(text).filter(([k, v]) => defaults && v !== defaults[k]));
      await api.put(`/jobs/${job.id}/request-letter/text`, { ...edits, include_details: includeDetails });
      const url = `${api.defaults.baseURL}/jobs/${job.id}/request-letter.pdf?v=${Date.now()}`;
      if (win) { win.location.href = url; }
      else {
        const r = await api.get(`/jobs/${job.id}/request-letter.pdf`, { params: { v: Date.now() }, responseType: "blob" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(r.data); a.download = `records-request-${job.job_number || job.id}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      }
      toast.success("Request letter generated");
      onClose();
    } catch (e) { if (win) win.close(); toast.error(e?.response?.data?.detail || "Failed to generate"); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="request-letter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" />Records request letter</DialogTitle>
          <DialogDescription>Tweak the wording below, then generate. Edits are saved with the job so the next PDF uses them. Date, office address, record details and your letterhead fill in automatically.</DialogDescription>
        </DialogHeader>
        {!defaults ? <div className="text-sm text-slate-500 py-6">Loading…</div> : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {FIELDS.map(([k, label, Comp]) => (
              <div key={k}>
                <div className="flex items-center justify-between mb-1">
                  <div className="sm-label">{label}</div>
                  {text[k] !== defaults[k] && <button type="button" className="sm-link text-xs inline-flex items-center gap-1" onClick={() => setText((t) => ({ ...t, [k]: defaults[k] }))} data-testid={`request-letter-reset-${k}`}><RotateCcw className="h-3 w-3" />Reset</button>}
                </div>
                <Comp value={text[k] || ""} onChange={(e) => setText((t) => ({ ...t, [k]: e.target.value }))} rows={k === "body" ? 7 : 3} className="bg-white text-sm" data-testid={`request-letter-${k}`} />
              </div>
            ))}
            <label className={`flex items-start gap-2 rounded-md border p-3 ${details ? "border-slate-200 bg-slate-50 cursor-pointer" : "border-dashed border-slate-200 text-slate-400"}`} data-testid="request-letter-details-label">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-emerald-700" checked={includeDetails && !!details} disabled={!details} onChange={(e) => setIncludeDetails(e.target.checked)} data-testid="request-letter-include-details" />
              <span className="text-sm"><span className="font-medium">Include the client's "Additional Details" in the letter</span>
                <span className="block text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{details ? `"${details}"` : "The client didn't enter any additional details on this request."}</span></span>
            </label>
            <div className="text-xs text-slate-500">Blank lines separate paragraphs. "Sincerely," and your name are added automatically.</div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="request-letter-cancel">Cancel</Button>
          <Button className="sm-green" disabled={busy || !defaults} onClick={generate} data-testid="request-letter-generate">{busy ? "Generating…" : "Generate PDF"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
